/**
 * SettingsPage
 * Kitchly v0.5.5
 *
 * v0.5.5: Added admin panel link (visible to is_admin users only).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Lock,
  Globe,
  Info,
  LogOut,
  ChevronRight,
  Eye,
  EyeOff,
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Shield,
  X,
  Check,
  MessageCircle,
  Settings as SettingsIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { auth, locations as locationsApi } from '../services/api';

function SettingsPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPw: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Locations state
  const [myLocations, setMyLocations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [showLocations, setShowLocations] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: '', location_type_id: '' });
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [locLoading, setLocLoading] = useState(false);

  useEffect(() => {
    if (showLocations) {
      loadLocations();
    }
  }, [showLocations]);

  const loadLocations = async () => {
    try {
      const [locsRes, typesRes] = await Promise.all([
        locationsApi.list(),
        locationsApi.types()
      ]);
      setMyLocations(locsRes.locations || []);
      setLocationTypes(typesRes.types || []);
    } catch (err) {
      toast.error('Failed to load locations');
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLoc.name.trim() || !newLoc.location_type_id) return;

    setLocLoading(true);
    try {
      await locationsApi.create({
        name: newLoc.name.trim(),
        location_type_id: parseInt(newLoc.location_type_id)
      });
      toast.success('Location added');
      setNewLoc({ name: '', location_type_id: '' });
      setShowAddForm(false);
      loadLocations();
    } catch (err) {
      toast.error(err.message || 'Failed to add location');
    } finally {
      setLocLoading(false);
    }
  };

  const handleEditLocation = async (id) => {
    if (!editName.trim()) return;

    setLocLoading(true);
    try {
      await locationsApi.update(id, { name: editName.trim() });
      toast.success('Location updated');
      setEditingId(null);
      setEditName('');
      loadLocations();
    } catch (err) {
      toast.error(err.message || 'Failed to update location');
    } finally {
      setLocLoading(false);
    }
  };

  const handleDeleteLocation = async (loc) => {
    if (loc.is_system_default) {
      toast.error('Cannot delete system default locations');
      return;
    }
    if (loc.item_count > 0) {
      toast.error('Move or remove items first (' + loc.item_count + ' items)');
      return;
    }
    if (!window.confirm('Delete "' + loc.name + '"? This cannot be undone.')) return;

    setLocLoading(true);
    try {
      await locationsApi.remove(loc.id);
      toast.success('Location deleted');
      setMyLocations(prev => prev.filter(l => l.id !== loc.id));
    } catch (err) {
      toast.error(err.message || 'Failed to delete location');
    } finally {
      setLocLoading(false);
    }
  };

  const startEdit = (loc) => {
    if (loc.is_system_default) {
      toast.error('System default locations cannot be renamed');
      return;
    }
    setEditingId(loc.id);
    setEditName(loc.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwords.newPw !== passwords.confirm) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }

    if (passwords.newPw.length < 8) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      await auth.changePassword({
        current_password: passwords.current,
        new_password: passwords.newPw
      });
      toast.success(t('auth.passwordChanged'));
      setPasswords({ current: '', newPw: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm(t('settings.logoutConfirm'))) {
      logout();
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
      </div>

      {/* === ALPHA FEEDBACK BANNER === */}
      <div
        onClick={() => navigate('/feedback', { state: { from: 'Settings' } })}
        style={{
          background: 'linear-gradient(135deg, #7A9E7E 0%, #6B8E6F 100%)',
          color: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-md) var(--space-lg)',
          marginBottom: 'var(--space-lg)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-md)',
          boxShadow: '0 2px 8px rgba(122, 158, 126, 0.3)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease'
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/feedback', { state: { from: 'Settings' } })}
      >
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius-full)',
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <MessageCircle size={22} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 2 }}>
            {t('feedback.bannerTitle')}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>
            {t('feedback.bannerSubtitle')}
          </div>
        </div>
        <ChevronRight size={20} style={{ opacity: 0.7 }} />
      </div>

      {/* Account Section */}
      <div className="card mb-lg">
        <h3 className="card-title mb-md">{t('settings.account')}</h3>

        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)' }}>
            <User size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.displayName')}</div>
            <div className="list-item-title">{user?.display_name}</div>
          </div>
        </div>

        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-secondary-light)', color: 'var(--color-secondary-dark)' }}>
            <Mail size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.email')}</div>
            <div className="list-item-title">{user?.email}</div>
          </div>
        </div>

        <div
          className="list-item"
          onClick={() => setShowPasswordForm(!showPasswordForm)}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <div className="list-item-icon" style={{ background: '#FFF3E0', color: 'var(--color-accent)' }}>
            <Lock size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-title">{t('settings.changePassword')}</div>
          </div>
          <ChevronRight size={18} color="var(--color-text-light)" />
        </div>
      </div>

      {/* Change Password Form */}
      {showPasswordForm && (
        <div className="card mb-lg">
          <h3 className="card-title mb-md">{t('settings.changePassword')}</h3>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="form-label">{t('auth.currentPassword')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrent ? 'text' : 'password'}
                  className="form-input"
                  value={passwords.current}
                  onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => setShowCurrent(!showCurrent)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('auth.newPassword')}</label>
              <input
                type="password"
                className="form-input"
                value={passwords.newPw}
                onChange={(e) => setPasswords(p => ({ ...p, newPw: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                className="form-input"
                value={passwords.confirm}
                onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowPasswordForm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Manage Locations Section */}
      <div className="card mb-lg">
        <div
          className="list-item"
          onClick={() => setShowLocations(!showLocations)}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <div className="list-item-icon" style={{ background: '#E8F5E9', color: '#4CAF50' }}>
            <MapPin size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-title">Manage Locations</div>
            <div className="list-item-subtitle">Add, edit, or remove storage locations</div>
          </div>
          <ChevronRight
            size={18}
            color="var(--color-text-light)"
            style={{ transform: showLocations ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
          />
        </div>
      </div>

      {showLocations && (
        <div className="card mb-lg">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
            <h3 className="card-title" style={{ margin: 0 }}>Your Locations</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddForm(!showAddForm)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: '13px' }}
            >
              <Plus size={16} />
              Add
            </button>
          </div>

          {/* Add New Location Form */}
          {showAddForm && (
            <form onSubmit={handleAddLocation} style={{
              background: 'var(--color-bg-input)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-md)'
            }}>
              <div className="form-group" style={{ marginBottom: 'var(--space-sm)' }}>
                <label className="form-label">Location Type</label>
                <select
                  className="form-input"
                  value={newLoc.location_type_id}
                  onChange={(e) => setNewLoc(n => ({ ...n, location_type_id: e.target.value }))}
                  required
                >
                  <option value="">Select a type...</option>
                  {locationTypes.map(lt => (
                    <option key={lt.id} value={lt.id}>{lt.name} ({lt.temperature_category})</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-sm)' }}>
                <label className="form-label">Location Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Garage Freezer, Upstairs Bathroom..."
                  value={newLoc.name}
                  onChange={(e) => setNewLoc(n => ({ ...n, name: e.target.value }))}
                  required
                  maxLength={100}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={locLoading}>
                  {locLoading ? 'Adding...' : 'Add Location'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowAddForm(false); setNewLoc({ name: '', location_type_id: '' }); }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Location List */}
          {myLocations.length === 0 ? (
            <p style={{ color: 'var(--color-text-light)', textAlign: 'center', padding: 'var(--space-md)' }}>
              No locations yet. Add your first one above.
            </p>
          ) : (
            myLocations.map((loc) => (
              <div key={loc.id} className="list-item" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <div
                  className="list-item-icon"
                  style={{ background: loc.color + '22', color: loc.color, flexShrink: 0 }}
                >
                  <MapPin size={16} />
                </div>

                {editingId === loc.id ? (
                  /* Edit mode */
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', fontSize: '14px' }}
                      autoFocus
                      maxLength={100}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditLocation(loc.id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                    <button
                      className="btn-icon"
                      onClick={() => handleEditLocation(loc.id)}
                      disabled={locLoading}
                      style={{ color: '#4CAF50', padding: 4 }}
                    >
                      <Check size={18} />
                    </button>
                    <button className="btn-icon" onClick={cancelEdit} style={{ color: '#999', padding: 4 }}>
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  /* Display mode */
                  <>
                    <div className="list-item-content" style={{ minWidth: 0 }}>
                      <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {loc.name}
                        {loc.is_system_default ? (
                          <Shield size={13} color="var(--color-text-light)" title="System default" />
                        ) : null}
                      </div>
                      <div className="list-item-subtitle">
                        {loc.type_name} &middot; {loc.item_count || 0} items
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {!loc.is_system_default && (
                        <button
                          className="btn-icon"
                          onClick={() => startEdit(loc)}
                          style={{ color: 'var(--color-text-secondary)', padding: 6 }}
                          title="Rename"
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {!loc.is_system_default && (
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteLocation(loc)}
                          style={{ color: '#e74c3c', padding: 6 }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          )}

          <p style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: 'var(--space-sm)', padding: '0 var(--space-xs)' }}>
            <Shield size={11} style={{ verticalAlign: 'middle' }} /> System defaults cannot be edited or deleted.
          </p>
        </div>
      )}

      {/* Language Section */}
      <div className="card mb-lg">
        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)' }}>
            <Globe size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.language')}</div>
            <div className="list-item-title">English</div>
          </div>
          <span className="badge badge-primary">Phase 2</span>
        </div>
      </div>

      {/* Admin Section — only visible to admins */}
      {!!user?.is_admin && (
        <div className="card mb-lg">
          <div
            className="list-item"
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
          >
            <div className="list-item-icon" style={{ background: '#FFF3E0', color: '#FF9800' }}>
              <SettingsIcon size={18} />
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{t('admin.title')}</div>
              <div className="list-item-subtitle">{t('admin.subtitle')}</div>
            </div>
            <ChevronRight size={18} color="var(--color-text-light)" />
          </div>
        </div>
      )}

      {/* About Section */}
      <div className="card mb-lg">
        <div className="list-item">
          <div className="list-item-icon" style={{ background: 'var(--color-bg-input)', color: 'var(--color-text-secondary)' }}>
            <Info size={18} />
          </div>
          <div className="list-item-content">
            <div className="list-item-subtitle">{t('settings.about')}</div>
            <div className="list-item-title">{t('settings.version')} 0.5.5</div>
          </div>
        </div>
      </div>

      {/* Logout */}
      <button className="btn btn-danger btn-full btn-lg" onClick={handleLogout}>
        <LogOut size={20} />
        {t('settings.logout')}
      </button>
    </div>
  );
}

export default SettingsPage;
